"use client";

import { useEffect, useRef } from "react";
import type { Metric } from "@/lib/data/metrics";

export function CountUp({
  value,
  prefix = "",
  suffix = "",
  duration = 1400,
}: {
  value: number;
  prefix?: string;
  suffix?: string;
  duration?: number;
}) {
  const numRef = useRef<HTMLSpanElement>(null);
  const started = useRef(false);

  useEffect(() => {
    const node = numRef.current;
    if (!node) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      node.textContent = String(value);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || started.current) return;
          started.current = true;
          const start = performance.now();
          const tick = (time: number) => {
            const progress = Math.min(1, (time - start) / duration);
            const eased = 1 - Math.pow(1 - progress, 3);
            node.textContent = String(Math.round(value * eased));
            if (progress < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.5 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [value, duration]);

  return (
    <span className="metric-value">
      {prefix}
      <span ref={numRef}>0</span>
      <small>{suffix}</small>
    </span>
  );
}

export function MetricStat({ metric }: { metric: Metric }) {
  return (
    <article>
      <CountUp value={metric.value} prefix={metric.prefix ?? ""} suffix={metric.suffix} />
      <p>{metric.label}</p>
      <small>{metric.description}</small>
    </article>
  );
}